import React, { useState, useRef } from 'react';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';
const headingFont = { fontFamily: 'Sora, sans-serif', fontWeight: 400 };
const bodyFont = { fontFamily: 'Montserrat, sans-serif', fontWeight: 400 };
const buttonFont = { fontFamily: 'Montserrat, sans-serif', fontWeight: 700 };
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

async function fetchPlacePredictions(input) {
  if (!input || !GOOGLE_API_KEY) {
    console.log('🚫 fetchPlacePredictions skipped:', { input: !!input, hasKey: !!GOOGLE_API_KEY });
    return [];
  }
  try {
    console.log('🌐 Fetching place predictions for:', input);
    const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(input)}&types=address&key=${GOOGLE_API_KEY}`;
    console.log('🔗 API URL:', url);
    
    // Try direct fetch first (works on mobile if API key is properly configured)
    let res;
    try {
      console.log('🌐 Attempting direct fetch to:', url);
      res = await fetch(url);
      console.log('📡 Direct fetch response status:', res.status);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
    } catch (directError) {
      console.log('📡 Direct fetch failed, trying CORS proxy:', directError.message);
      // Fallback to CORS proxy
      try {
        res = await fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`);
        console.log('📡 CORS proxy response status:', res.status);
        if (!res.ok) {
          throw new Error(`CORS proxy HTTP ${res.status}: ${res.statusText}`);
        }
      } catch (corsError) {
        console.log('📡 CORS proxy also failed:', corsError.message);
        throw corsError;
      }
    }
    
    const data = await res.json();
    console.log('📊 API Response:', data);
    return data.predictions || [];
  } catch (error) {
    console.log('❌ Google Places API error:', error);
    return [];
  }
}

async function fetchPlaceDetails(placeId) {
  if (!GOOGLE_API_KEY) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=formatted_address,address_component,geometry&key=${GOOGLE_API_KEY}`;
    
    // Try direct fetch first, fallback to CORS proxy
    let res;
    try {
      res = await fetch(url);
    } catch (directError) {
      console.log('📡 Direct fetch failed for place details, trying CORS proxy:', directError.message);
      res = await fetch(`https://corsproxy.io/?${encodeURIComponent(url)}`);
    }
    
    const data = await res.json();
    return data.result || null;
  } catch (error) {
    console.log('Google Places API error:', error);
    return null;
  }
}

function parseAddressComponents(components) {
  const get = (type) => {
    const comp = components.find(c => c.types.includes(type));
    return comp ? comp.long_name : '';
  };
  return {
    street: [get('street_number'), get('route')].filter(Boolean).join(' '),
    city: get('locality') || get('sublocality') || get('administrative_area_level_2'),
    state: get('administrative_area_level_1'),
    zip: get('postal_code'),
    country: get('country'),
  };
}

export default function ShopLocationIntroStep({ onNext, onBack, isFirst }) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null); // { address, components, lat, lng }
  const [touched, setTouched] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  });
  const inputRef = useRef();

  // Check if Google Places API is available
  const hasGoogleAPI = !!GOOGLE_API_KEY;
  
  // Debug logging
  console.log('🔍 Google Places API Debug:', {
    GOOGLE_API_KEY: GOOGLE_API_KEY ? 'Present' : 'Missing',
    hasGoogleAPI,
    input,
    suggestions: suggestions.length,
    allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
    userAgent: navigator.userAgent,
    isSecureContext: window.isSecureContext
  });

  // Debounced fetch for suggestions
  React.useEffect(() => {
    if (!hasGoogleAPI) return;
    
    let active = true;
    if (!input || selected?.address === input) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    setError('');
    const timeout = setTimeout(async () => {
      try {
        const preds = await fetchPlacePredictions(input);
        if (active) setSuggestions(preds);
      } catch (e) {
        if (active) setSuggestions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 400);
    return () => { active = false; clearTimeout(timeout); };
  }, [input, selected, hasGoogleAPI]);

  async function handleSelect(pred) {
    setLoading(true);
    setError('');
    try {
      const details = await fetchPlaceDetails(pred.place_id);
      if (!details) throw new Error('No details');
      const components = parseAddressComponents(details.address_components || []);
      setSelected({
        address: details.formatted_address,
        ...components,
        lat: details.geometry?.location?.lat,
        lng: details.geometry?.location?.lng,
      });
      setInput(details.formatted_address);
      setSuggestions([]);
    } catch (e) {
      setError('Could not fetch address details.');
    } finally {
      setLoading(false);
    }
  }

  function handleInput(e) {
    setInput(e.target.value);
    setTouched(true);
    setSelected(null);
  }

  function handleManualInputChange(field, value) {
    setManualAddress(prev => ({ ...prev, [field]: value }));
  }

  function handleManualSubmit() {
    const { street, city, state, zip } = manualAddress;
    if (!street.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setError('Please fill in all address fields.');
      return;
    }
    
    // Create a selected address object from manual input
    const address = `${street}, ${city}, ${state} ${zip}`;
    setSelected({
      address,
      street: street.trim(),
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim(),
      lat: null,
      lng: null
    });
    setInput(address);
    setShowManualInput(false);
    setError('');
  }

  function handleNext() {
    if (selected) onNext({ shopAddress: selected });
  }

  const isValid = !!selected;

  return (
    <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
      <div style={{ width: '100%', maxWidth: 400, margin: '0 auto', paddingTop: 32, position: 'relative' }}>
        <div style={{ ...headingFont, fontSize: TYPOGRAPHY.ONBOARDING.H2, background: COLORS.PRIMARY_GRADIENT, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 18, lineHeight: 1.1 }}>Where is your place<br />located?</div>
        <div style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#222', marginBottom: 32, lineHeight: 1.5 }}>
          Your address will only be shared with those who are picking up an order and to give general location for shoppers.
        </div>
        <label htmlFor="shop-address" style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: COLORS.PRIMARY_GRADIENT_START, fontWeight: 700, marginBottom: 6, display: 'block' }}>Shop Address</label>
        
        {!hasGoogleAPI && (
          <div style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#666', marginBottom: 16, padding: '12px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
            ⚠️ Address autocomplete is not available. You can enter your address manually below.
            <br />
            <small>Debug: hasGoogleAPI = {hasGoogleAPI.toString()}, GOOGLE_API_KEY = {GOOGLE_API_KEY ? 'Present' : 'Missing'}</small>
          </div>
        )}
        
        <div style={{ position: 'relative' }}>
          <input
            id="shop-address"
            type="text"
            value={input}
            onChange={handleInput}
            placeholder={hasGoogleAPI ? "Enter your shop address" : "Enter your shop address manually"}
            style={{
              width: '100%',
              padding: '14px 12px',
              borderRadius: 10,
              border: `2px solid ${!isValid && touched ? '#F44336' : '#eee'}`,
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              ...bodyFont,
              marginBottom: 8,
              background: '#FAF9F7',
              color: '#222',
              outline: 'none',
              transition: 'border 0.2s',
            }}
            autoComplete="off"
            aria-label="Shop Address"
            ref={inputRef}
          />
          
          {/* Suggestions Dropdown - Only show if Google API is available */}
          {hasGoogleAPI && suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px #0002', border: '1px solid #eee', maxHeight: 220, overflowY: 'auto' }}>
              {suggestions.map(pred => (
                <div
                  key={pred.place_id}
                  onClick={() => handleSelect(pred)}
                  style={{ padding: '14px 18px', cursor: 'pointer', ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, color: '#222', borderBottom: '1px solid #F2E3D7' }}
                  onMouseDown={e => e.preventDefault()}
                >
                  {pred.description}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Manual Address Input Fallback */}
        {!hasGoogleAPI && !showManualInput && (
          <button
            type="button"
            onClick={() => setShowManualInput(true)}
            style={{
              background: 'transparent',
              color: COLORS.PRIMARY_GRADIENT_START,
              border: `1px solid ${COLORS.PRIMARY_GRADIENT_START}`,
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
              cursor: 'pointer',
              marginBottom: 16,
              ...bodyFont
            }}
          >
            Enter Address Manually
          </button>
        )}
        
        {!hasGoogleAPI && showManualInput && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#666', marginBottom: 4, display: 'block' }}>Street Address</label>
                <input
                  type="text"
                  value={manualAddress.street}
                  onChange={(e) => handleManualInputChange('street', e.target.value)}
                  placeholder="123 Main St"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                    ...bodyFont
                  }}
                />
              </div>
              <div>
                <label style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#666', marginBottom: 4, display: 'block' }}>City</label>
                <input
                  type="text"
                  value={manualAddress.city}
                  onChange={(e) => handleManualInputChange('city', e.target.value)}
                  placeholder="City"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                    ...bodyFont
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#666', marginBottom: 4, display: 'block' }}>State</label>
                <input
                  type="text"
                  value={manualAddress.state}
                  onChange={(e) => handleManualInputChange('state', e.target.value)}
                  placeholder="State"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                    ...bodyFont
                  }}
                />
              </div>
              <div>
                <label style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: '#666', marginBottom: 4, display: 'block' }}>ZIP Code</label>
                <input
                  type="text"
                  value={manualAddress.zip}
                  onChange={(e) => handleManualInputChange('zip', e.target.value)}
                  placeholder="12345"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #ddd',
                    fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                    ...bodyFont
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleManualSubmit}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                  cursor: 'pointer',
                  ...bodyFont
                }}
              >
                Use This Address
              </button>
              <button
                type="button"
                onClick={() => setShowManualInput(false)}
                style={{
                  background: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: '10px 16px',
                  fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION,
                  cursor: 'pointer',
                  ...bodyFont
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {loading && <div style={{ ...bodyFont, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, color: COLORS.PRIMARY_GRADIENT_START, marginTop: 6 }}>Loading…</div>}
        {error && <div style={{ color: '#F44336', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, minHeight: 20, ...bodyFont }}>{error}</div>}
        {!isValid && touched && !error && hasGoogleAPI && (
          <div style={{ color: '#F44336', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, minHeight: 20, ...bodyFont }}>Address is required. Please select a suggestion.</div>
        )}
        {!isValid && touched && !error && !hasGoogleAPI && (
          <div style={{ color: '#F44336', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, minHeight: 20, ...bodyFont }}>Address is required. Please enter your address manually.</div>
        )}
      </div>
      <div style={{ width: '100%', maxWidth: 400, margin: '0 auto', paddingBottom: 16, marginTop: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          {!isFirst && (
            <button
              type="button"
              onClick={onBack}
              style={{
                background: '#fff',
                color: '#9CA3AF',
                border: 'none',
                borderRadius: 10,
                padding: '14px 32px',
                fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 44,
                minWidth: 120,
                transition: 'all 0.2s',
              }}
              aria-label="Back"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={handleNext}
            disabled={!isValid}
            style={{
              background: isValid ? COLORS.PRIMARY_GRADIENT : '#E0CFC2',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '14px 32px',
              fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
              fontWeight: 600,
              cursor: isValid ? 'pointer' : 'not-allowed',
              minHeight: 44,
              minWidth: 120,
              boxShadow: '0 1px 4px #0001',
              transition: 'all 0.2s',
            }}
            aria-label="Next"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
} 